import React, { useState } from 'react';
import type { CatalogQuestion } from '../types';

interface QuestionEditDrawerProps {
  question: CatalogQuestion | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (updated: Partial<CatalogQuestion>) => void;
}

export const QuestionEditDrawer: React.FC<QuestionEditDrawerProps> = ({
  question,
  isOpen,
  onClose,
  onSave,
}) => {
  if (!isOpen || !question) return null;

  const [label, setLabel] = useState(question.label);
  const [fieldType, setFieldType] = useState(question.field_type);
  const [placeholder, setPlaceholder] = useState(question.placeholder || '');
  const [helpText, setHelpText] = useState(question.help_text || '');
  const [optionsStr, setOptionsStr] = useState(question.options.join(', '));
  const [required, setRequired] = useState(question.required);
  const [requiredWhenVisible, setRequiredWhenVisible] = useState(question.required_when_visible);
  const [stage, setStage] = useState(question.stage);
  const [block, setBlock] = useState(question.block);

  const handleSave = () => {
    const opts = optionsStr
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    onSave({
      question_id: question.question_id,
      service_id: question.service_id,
      label,
      field_type: fieldType,
      placeholder: placeholder || null,
      help_text: helpText || null,
      options: opts,
      required,
      required_when_visible: requiredWhenVisible,
      stage,
      block,
      status: 'CHANGED',
    });
    onClose();
  };

  return (
    <div className="cov-modal-overlay" onClick={onClose}>
      <div className="cov-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="cov-drawer-head">
          <div>
            <h3 style={{ margin: 0, fontSize: '1.125rem' }}>Редактирование вопроса</h3>
          </div>
          <button className="cov-btn cov-btn-outline" onClick={onClose}>✕</button>
        </div>

        <div className="cov-drawer-body">
          <div className="cov-form-group">
            <label className="cov-form-label">Текст вопроса / Название поля</label>
            <input
              className="cov-form-input"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <div className="cov-form-group">
              <label className="cov-form-label">Этап</label>
              <select
                className="cov-form-select"
                value={stage}
                onChange={(e) => setStage(Number(e.target.value))}
              >
                <option value={0}>Этап 0 (Способ начала)</option>
                <option value={1}>Этап 1 (Направление и услуга)</option>
                <option value={2}>Этап 2 (Сведения и материалы)</option>
                <option value={3}>Этап 3 (Проверка ответов)</option>
                <option value={4}>Этап 4 (Уточнения и ИИ)</option>
                <option value={5}>Этап 5 (Подтверждение)</option>
              </select>
            </div>
            <div className="cov-form-group">
              <label className="cov-form-label">Блок Этапа 2</label>
              <select
                className="cov-form-select"
                value={block}
                onChange={(e) => setBlock(e.target.value as any)}
              >
                <option value="A">Блок A: Задача и контекст</option>
                <option value="B">Блок B: Требования к результату</option>
                <option value="C">Блок C: Материалы и файлы</option>
                <option value="FLOW">Общие вопросы</option>
              </select>
            </div>
          </div>

          <div className="cov-form-group">
            <label className="cov-form-label">Тип поля</label>
            <select
              className="cov-form-select"
              value={fieldType}
              onChange={(e) => setFieldType(e.target.value)}
            >
              <option value="text">Текст (однострочный)</option>
              <option value="textarea">Текст (многострочный)</option>
              <option value="select">Выпадающий список</option>
              <option value="multiselect">Множественный выбор</option>
              <option value="radio">Переключатель</option>
              <option value="checkbox">Флажок</option>
              <option value="number">Число</option>
              <option value="date">Дата</option>
              <option value="file">Файл (одиночный)</option>
              <option value="file_multi">Файлы (множественные)</option>
            </select>
          </div>

          {['select', 'multiselect', 'radio'].includes(fieldType) && (
            <div className="cov-form-group">
              <label className="cov-form-label">Варианты выбора (через запятую)</label>
              <textarea
                className="cov-form-textarea"
                rows={3}
                value={optionsStr}
                onChange={(e) => setOptionsStr(e.target.value)}
              />
            </div>
          )}

          <div className="cov-form-group">
            <label className="cov-form-label">Подсказка в поле</label>
            <input
              className="cov-form-input"
              value={placeholder}
              onChange={(e) => setPlaceholder(e.target.value)}
            />
          </div>

          <div className="cov-form-group">
            <label className="cov-form-label">Пояснение к полю</label>
            <textarea
              className="cov-form-textarea"
              rows={2}
              value={helpText}
              onChange={(e) => setHelpText(e.target.value)}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8125rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={required}
                onChange={(e) => setRequired(e.target.checked)}
              />
              Обязательное поле
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8125rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={requiredWhenVisible}
                onChange={(e) => setRequiredWhenVisible(e.target.checked)}
              />
              Обязательно только если поле видно
            </label>
          </div>
        </div>

        <div className="cov-drawer-foot">
          <button className="cov-btn cov-btn-outline" onClick={onClose}>Отмена</button>
          <button className="cov-btn cov-btn-primary" onClick={handleSave}>Сохранить черновик</button>
        </div>
      </div>
    </div>
  );
};
